// @ts-strict-ignore
import { ColumnType } from 'antd/lib/table';
import Table from 'antd/lib/table/Table';
import * as React from 'react';
import { AppComponentProps } from '../../typescriptHelpers';
import Text from 'antd/lib/typography/Text';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import {
    selectEffectiveTimezone,
    selectReleasePilotRotation,
    selectIsPilotRotationSyncEnabled,
    selectSchedulingIntegrationForPilotRotation,
    selectOrgUser,
    selectWorkflowId,
    selectReleasePilotEligibleUsersForApp,
    selectUserCanPerformAction,
    selectAuthCredentials,
} from '../../selectors/selectors';
import * as API from '../../api';
import {
    ReleasePilotRotationItem,
    ReleasePilotRotationSyncInfo,
    ReleasePilotSwapPreview,
    ReleaseType,
} from '../../types';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import AddReleasePilotsModal from './AddReleasePilotsModal';
import PilotRotationSettingsModal from './PilotRotationSettingsModal';
import PilotMissingPermissionsWarning from '../PilotMissingPermissionsWarning';
import { displayReleasePilotName, displayUserNameLong } from '../../helpers/displayHelpers';
import {
    PlusOutlined,
    EditOutlined,
    RetweetOutlined,
    CheckOutlined,
    DeleteOutlined,
    MenuOutlined,
    RightOutlined,
    UserSwitchOutlined,
    InfoCircleOutlined,
    SettingOutlined,
} from '@ant-design/icons';
import Button from 'antd/lib/button';
import Dropdown from 'antd/lib/dropdown';
import Select from 'antd/lib/select';
import Space from 'antd/lib/space';
import { antGrey, runwayColors } from '../styles/globalStyles';
import Tooltip from 'antd/lib/tooltip';
import Typography from 'antd/lib/typography';
import PropTypes from 'prop-types';
import { dayjs as extendedDayjs } from '@/extendedDayjs';
import { DatetimeFormat } from '../../timeHelpers';
import { SortableHandle, SortableElement, SortableContainer } from 'react-sortable-hoc';
import { arrayMoveImmutable } from 'array-move';
import { tryUpdateAppSettings } from '../../actions/appSettings';
import { AppSettingsUpdatable } from '../../actions/updatable';
import { updatePilotRotationIndexes } from '../../helpers';
import { globalMessage } from '../../utils/globalMessage';
import Modal from 'antd/lib/modal';
import Popconfirm from 'antd/lib/popconfirm';
import {
    displayNameForIntegrationType,
    iconAndAltForIntegrationType,
    scheduleNameForSchedulingIntegration,
} from '@/helpers/integrationHelpers';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

const ReleasePilotRotationTable: React.FC<AppComponentProps> = (props: AppComponentProps) => {
    const DragHandle = SortableHandle((props: { style?: React.CSSProperties }) => (
        <MenuOutlined style={{ cursor: 'pointer', ...props.style }} size={21} />
    )) as React.ComponentType<{ style?: React.CSSProperties }>;
    const SortableTableItem = SortableElement((props) => <tr {...props} />);
    const SortableTableContainer = SortableContainer((props) => <tbody {...props} />);
    const dispatch = useDispatch();
    const [modal, modalContextHolder] = Modal.useModal();
    const [showAddReleasePilotsModal, setShowAddReleasePilotsModal] = React.useState(false);
    const [showEditView, setShowEditView] = React.useState(false);
    const releasePilotRotation = useSelector((state: RootState) => selectReleasePilotRotation(state, props.appId));
    const effectiveTimezone = useSelector((state: RootState) => selectEffectiveTimezone(state, props.appId));
    const releasePilotRotationItems = releasePilotRotation?.items ?? [];
    const syncInfo = releasePilotRotation.syncInfo;
    const isRotationSyncedFromScheduleIntegration = !!releasePilotRotation.syncInfo;
    const [isUpdateRotationButtonDisabled, setIsUpdateRotationButtonDisabled] = React.useState(true);

    const [showPilotRotationSettingsModal, setShowPilotRotationSettingsModal] = React.useState(false);
    const workflowId = useSelector((state: RootState) => selectWorkflowId(state, props.appId));

    const scheduleIntegration = useSelector(selectSchedulingIntegrationForPilotRotation(props.appId));
    const isExternalSyncEnabled = useSelector(selectIsPilotRotationSyncEnabled(props.appId));
    const disableManualActions = isRotationSyncedFromScheduleIntegration || isExternalSyncEnabled;
    const isSyncedFromScheduleIntegration = isExternalSyncEnabled && !!scheduleIntegration;

    // Sync info only lands once a refresh has run, so attribute the source from the chosen integration until then
    const syncSource: ReleasePilotRotationSyncInfo | undefined =
        syncInfo ??
        (isSyncedFromScheduleIntegration ?
            {
                integrationId: scheduleIntegration.id,
                scheduleName: scheduleNameForSchedulingIntegration(scheduleIntegration),
            }
        :   undefined);

    const firstCycleRotationValues = releasePilotRotationItems.filter((obj) => obj.pilotRotationIndex != null);
    const tableContainer: React.RefObject<HTMLDivElement> = React.createRef();

    const [editViewPilots, setEditViewPilots] = React.useState([]);

    const updateRotationIsLoading = useIsLoading(AppSettingsUpdatable.UpdateReleasePilotsOrder);
    const updateRotationSuccess = useUpdated(AppSettingsUpdatable.UpdateReleasePilotsOrder);
    const updateRotationError = useError(AppSettingsUpdatable.UpdateReleasePilotsOrder);

    const renderColumnTitle = (title: string, syncInfo?: ReleasePilotRotationSyncInfo) => {
        if (syncInfo) {
            const integrationId = syncInfo.integrationId;
            const integrationName = displayNameForIntegrationType(integrationId);
            const [intIcon, intAltText] = iconAndAltForIntegrationType(integrationId);
            const scheduleName = syncInfo.scheduleName;

            return (
                <Text strong>
                    <Tooltip
                        title={
                            <Text style={{ color: 'white' }}>
                                {scheduleName ?
                                    <>
                                        {`This app's release pilot rotation is synced from the `}
                                        <strong>{scheduleName}</strong> schedule in {integrationName}.
                                    </>
                                :   `This app's release pilot rotation is synced from ${integrationName}.`}
                            </Text>
                        }
                    >
                        <img
                            src={intIcon}
                            alt={intAltText}
                            width={20}
                            height={20}
                            style={{
                                marginRight: 8,
                            }}
                        />
                    </Tooltip>
                    {title}
                </Text>
            );
        }
        return <Text strong>{title}</Text>;
    };

    const renderTargetKickoffDate = (
        releaseVersionKickoffDate: string,
        releaseVersionTargetKickoffDate: string,
        isReleaseVersionTargetKickoffDatePredicted: boolean,
    ) => {
        const actualKickoff =
            releaseVersionKickoffDate ?
                extendedDayjs(releaseVersionKickoffDate).tz(effectiveTimezone).format(DatetimeFormat.ShortMonthDate)
            :   null;

        let targetKickoffNode = <Text type="secondary">Not set</Text>;

        if (releaseVersionTargetKickoffDate) {
            const targetKickoff = extendedDayjs(releaseVersionTargetKickoffDate)
                .tz(effectiveTimezone)
                .format(DatetimeFormat.ShortMonthDate);

            // Not yet supported
            // if (releasePilotRotation.isReleaseVersionTargetKickoffDatePredictedFromScheduleIntegration) {
            //     const scheduleIntegrationName = scheduleIntegration
            //         ? displayNameForIntegrationType(scheduleIntegration.id)
            //         : 'Schedule';
            //     const scheduleName = scheduleIntegration
            //         ? scheduleNameForSchedulingIntegration(scheduleIntegration)
            //         : '';

            //     targetKickoffNode = (
            //         <RenderPredictedText
            //             text={targetKickoff}
            //             tooltip={`This target kickoff is predicted based on your app's ${scheduleIntegrationName} integration's "${scheduleName}" schedule.`}
            //         />
            //     );
            // }

            if (isReleaseVersionTargetKickoffDatePredicted) {
                targetKickoffNode = (
                    <RenderPredictedText
                        text={targetKickoff}
                        tooltip="This target kickoff is predicted based on your app's schedule cadence."
                    />
                );
            } else {
                targetKickoffNode = <Text>{targetKickoff}</Text>;
            }
        }

        if (actualKickoff) {
            return (
                <Space>
                    {targetKickoffNode}
                    <Text type="secondary">(kicked off {actualKickoff})</Text>
                </Space>
            );
        }

        return targetKickoffNode;
    };

    const RenderPredictedText = ({ text, tooltip }) => (
        <Tooltip title={tooltip}>
            <Typography.Text style={{ color: antGrey.grey6 }}>{text}</Typography.Text>
        </Tooltip>
    );

    RenderPredictedText.propTypes = {
        text: PropTypes.string.isRequired,
        tooltip: PropTypes.string.isRequired,
    };

    const onSortEnd = ({ oldIndex, newIndex }) => {
        if (oldIndex !== newIndex) {
            const sortedRotation = arrayMoveImmutable(editViewPilots, oldIndex, newIndex);
            setEditViewPilots(sortedRotation);
            setIsUpdateRotationButtonDisabled(false);
        }
    };

    const draggableContainer = (props) => {
        return (
            <SortableTableContainer
                useDragHandle
                helperClass="row-dragging"
                lockAxis="y"
                helperContainer={tableContainer.current}
                onSortEnd={onSortEnd}
                {...props}
            />
        );
    };

    const draggableBodyRow = (props) => {
        const pilotRotationIndex = props['data-row-key'];
        const index = editViewPilots.findIndex((pilot) => pilot.pilotRotationIndex === pilotRotationIndex);

        return <SortableTableItem index={index} {...props} />;
    };

    const handleUpdateRotationOrder = () => {
        let updatedReleases = 0;
        const updatedPilotRotationIndexes = updatePilotRotationIndexes(firstCycleRotationValues, editViewPilots);
        const rearrangedCycle: ReleasePilotRotationItem[] = firstCycleRotationValues.map((rotationRow, index) => {
            const originalPilotRotationIndex = editViewPilots[index]['pilotRotationIndex'];
            const updatedPilotRotationIndex = updatedPilotRotationIndexes[index]['pilotRotationIndex'];
            const keepsItsOwnPilot = !!rotationRow.overridePilotId;
            if (
                !rotationRow.isReleaseVersionPredicted &&
                !keepsItsOwnPilot &&
                originalPilotRotationIndex != updatedPilotRotationIndex
            ) {
                updatedReleases += 1;
            }
            return {
                ...rotationRow,
                id: updatedPilotRotationIndexes[index]['id'],
                pilotRotationIndex: updatedPilotRotationIndexes[index]['pilotRotationIndex'],
                overridePilotId: updatedPilotRotationIndexes[index]['overridePilotId'],
            };
        });
        return { updatedReleases, rearrangedCycle };
    };

    const resetEditCycleValues = () => {
        const editCycleValues = releasePilotRotationItems
            .filter((pilotRotation) => pilotRotation.pilotRotationIndex != null)
            .map((obj) => ({
                id: obj.id,
                overridePilotId: obj.overridePilotId,
                pilotRotationIndex: obj.pilotRotationIndex,
                isReleaseVersionPredicted: obj.isReleaseVersionPredicted,
            }));

        setEditViewPilots(editCycleValues);
    };

    const saveRotationOrder = (rearrangedCycle) => {
        dispatch(
            tryUpdateAppSettings(AppSettingsUpdatable.UpdateReleasePilotsOrder, {
                orgId: props.orgId,
                appId: props.appId,
                releasePilotRotation: rearrangedCycle,
            }),
        );
    };

    const updateRotationConfirmationModal = (rearrangedCycle, updatedReleases) => {
        modal.confirm({
            title: 'Confirm pilot rotation update',
            icon: null,
            content: `The release pilot for ${updatedReleases} active release(s) will be updated immediately.`,
            okText: 'Update',
            okButtonProps: {
                type: 'primary',
                loading: updateRotationIsLoading,
            },
            cancelText: 'Cancel',
            styles: { content: { padding: 24 } },
            onOk: () => saveRotationOrder(rearrangedCycle),
        });
    };

    const columns: ColumnType<ReleasePilotRotationItem>[] = [
        {
            title: '',
            dataIndex: 'index',
            key: 'index',
            render: (_, __, index) => (
                <div
                    style={{
                        width: '30px',
                        height: '30px',
                        borderRadius: '50%',
                        backgroundColor: antGrey.grey3,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                    }}
                >
                    {index + 1}
                </div>
            ),
        },
        {
            title: renderColumnTitle('Release version'),
            render: (releasePilotRotation: ReleasePilotRotationItem) => {
                const releaseVersion = releasePilotRotation.releaseVersion;
                if (releaseVersion && releasePilotRotation.isReleaseVersionPredicted) {
                    return (
                        <RenderPredictedText
                            text={releaseVersion}
                            tooltip="This version is predicted based on your app's default release type."
                        />
                    );
                }
                return <Text>{releaseVersion}</Text>;
            },
            width: '130px',
        },
        {
            title: renderColumnTitle('Kickoff'),
            render: (releasePilotRotation: ReleasePilotRotationItem) => {
                return renderTargetKickoffDate(
                    releasePilotRotation.releaseVersionKickoffDate,
                    releasePilotRotation.releaseVersionTargetKickoffDate,
                    releasePilotRotation.isReleaseVersionTargetKickoffDatePredicted,
                );
            },
        },
        {
            title: renderColumnTitle('Release pilot', syncSource),
            render: (releasePilotRotation: ReleasePilotRotationItem) => {
                return (
                    <ReleasePilotScheduleRotationRow
                        orgId={props.orgId}
                        appId={props.appId}
                        isSyncedFromScheduleIntegration={isSyncedFromScheduleIntegration}
                        effectiveTimezone={effectiveTimezone}
                        {...releasePilotRotation}
                    />
                );
            },
        },
        {
            title: '',
            align: 'right',
            render: (releasePilotRotation: ReleasePilotRotationItem) => {
                if (
                    releasePilotRotation.releaseType == ReleaseType.Hotfix ||
                    releasePilotRotation.releaseType == ReleaseType.Rollback
                ) {
                    return (
                        <Text
                            style={{
                                textAlign: 'right',
                                fontWeight: 'lighter',
                                color: antGrey.grey6,
                                fontSize: '12px',
                            }}
                        >
                            OUTSIDE OF <br /> ROTATION
                        </Text>
                    );
                }
                return (
                    <SwapReleasePilotAction
                        orgId={props.orgId}
                        appId={props.appId}
                        row={releasePilotRotation}
                        disabled={disableManualActions}
                    />
                );
            },
        },
    ];

    const editViewReleasesColumns: ColumnType<unknown>[] = [
        {
            title: '',
            dataIndex: 'index',
            key: 'index',
            render: (_, __, index) => (
                <div
                    style={{
                        width: '30px',
                        height: '30px',
                        borderRadius: '50%',
                        backgroundColor: antGrey.grey3,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                    }}
                >
                    {index + 1}
                </div>
            ),
        },
        {
            title: renderColumnTitle('Release version'),
            render: (releasePilotRotation: ReleasePilotRotationItem) => {
                const releaseVersion = releasePilotRotation.releaseVersion;
                if (releaseVersion && releasePilotRotation.isReleaseVersionPredicted) {
                    return (
                        <div style={{ height: '30px' }}>
                            <RenderPredictedText
                                text={releaseVersion}
                                tooltip="This version is predicted based on your app's default release type."
                            />
                        </div>
                    );
                }
                return (
                    <div style={{ height: '30px' }}>
                        <Text>{releaseVersion}</Text>
                    </div>
                );
            },
            width: '130px',
        },
        {
            title: renderColumnTitle('Target kickoff', syncSource),
            render: (releasePilotRotation: ReleasePilotRotationItem) => {
                return renderTargetKickoffDate(
                    releasePilotRotation.releaseVersionKickoffDate,
                    releasePilotRotation.releaseVersionTargetKickoffDate,
                    releasePilotRotation.isReleaseVersionTargetKickoffDatePredicted,
                );
            },
        },
    ];

    const editViewPilotsColumns: ColumnType<ReleasePilotRotationItem>[] = [
        {
            title: '',
        },
        {
            title: renderColumnTitle('Release pilot', syncSource),
            className: 'drag-visible',
            render: (releasePilotRotationRow) => {
                return <ReleasePilotRotationRow {...releasePilotRotationRow} orgId={props.orgId} />;
            },
        },
        {
            title: '',
            align: 'right',
            render: (releasePilotRotation: ReleasePilotRotationItem) => {
                if (showEditView) {
                    return (
                        <Space align="center" style={{ height: 30 }}>
                            <DeleteUserFromPilotRotationButton
                                orgId={props.orgId}
                                appId={props.appId}
                                releasePilotId={releasePilotRotation.id}
                            />
                            <DragHandle style={{ color: antGrey.grey7 }} />
                        </Space>
                    );
                }
                return <></>;
            },
        },
    ];

    React.useEffect(() => {
        if (updateRotationSuccess) {
            globalMessage.success('Release pilot rotation updated!');
            setShowEditView(false);
            setIsUpdateRotationButtonDisabled(true);
        }
    }, [updateRotationSuccess]);

    React.useEffect(() => {
        if (updateRotationError) {
            globalMessage.error(updateRotationError);
        }
    }, [updateRotationError]);

    React.useEffect(() => {
        resetEditCycleValues();
    }, [releasePilotRotation]);

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                {!showEditView && (
                    <Space direction="horizontal">
                        {disableManualActions && (
                            <Text type="secondary">
                                Go to{' '}
                                {scheduleIntegration ?
                                    displayNameForIntegrationType(scheduleIntegration.id)
                                :   'your Schedule integration'}{' '}
                                to edit this rotation
                            </Text>
                        )}
                        <PermissionedActionButton
                            disabled={disableManualActions}
                            actionType={RunwayUserAction.UAUpdateAppSettings}
                            onClick={() => setShowAddReleasePilotsModal(true)}
                        >
                            <PlusOutlined />
                            Add
                        </PermissionedActionButton>
                        <PermissionedActionButton
                            disabled={disableManualActions}
                            actionType={RunwayUserAction.UAUpdateAppSettings}
                            onClick={() => setShowEditView(true)}
                        >
                            <EditOutlined />
                            Edit
                        </PermissionedActionButton>
                        {workflowId && (
                            <Button icon={<SettingOutlined />} onClick={() => setShowPilotRotationSettingsModal(true)}>
                                Settings
                            </Button>
                        )}
                    </Space>
                )}
                {showEditView && (
                    <Space direction="horizontal">
                        <PermissionedActionButton
                            actionType={RunwayUserAction.UAUpdateAppSettings}
                            onClick={() => {
                                resetEditCycleValues();
                                setShowEditView(false);
                                setIsUpdateRotationButtonDisabled(true);
                            }}
                        >
                            Cancel
                        </PermissionedActionButton>
                        <PermissionedActionButton
                            actionType={RunwayUserAction.UAUpdateAppSettings}
                            disabled={isUpdateRotationButtonDisabled}
                            loading={updateRotationIsLoading}
                            onClick={() => {
                                const { updatedReleases, rearrangedCycle } = handleUpdateRotationOrder();
                                if (updatedReleases > 0) {
                                    updateRotationConfirmationModal(rearrangedCycle, updatedReleases);
                                } else {
                                    saveRotationOrder(rearrangedCycle);
                                }
                            }}
                            type="primary"
                        >
                            <CheckOutlined />
                            Update rotation
                        </PermissionedActionButton>
                    </Space>
                )}
            </div>
            {!showEditView && (
                <Table
                    size="middle"
                    locale={{
                        emptyText: 'No release pilots assigned for this app',
                    }}
                    columns={columns}
                    dataSource={releasePilotRotationItems}
                    pagination={false}
                    rowClassName={(record, _) =>
                        record.releaseType === ReleaseType.Hotfix ?
                            'release-pilot-rotation-table-hotfix-background-color'
                        :   ''
                    }
                />
            )}
            {showEditView && (
                <div style={{ display: 'flex', alignItems: 'stretch' }}>
                    <Table
                        size="middle"
                        style={{ flexGrow: 1, borderCollapse: 'collapse', borderSpacing: 0 }}
                        locale={{
                            emptyText: 'No release pilots assigned for this app',
                        }}
                        columns={editViewReleasesColumns}
                        dataSource={firstCycleRotationValues}
                        pagination={false}
                        rowClassName="release-pilot-rotation-table-custom-row"
                    />
                    <Table
                        size="middle"
                        style={{ flexGrow: 1, borderCollapse: 'collapse', borderSpacing: 0 }}
                        locale={{
                            emptyText: 'No release pilots assigned for this app',
                        }}
                        rowKey={'pilotRotationIndex'}
                        columns={editViewPilotsColumns}
                        dataSource={editViewPilots}
                        pagination={false}
                        rowClassName="release-pilot-rotation-table-custom-row"
                        components={{
                            body: {
                                wrapper: draggableContainer,
                                row: draggableBodyRow,
                            },
                        }}
                    />
                </div>
            )}
            <AddReleasePilotsModal
                open={showAddReleasePilotsModal}
                appId={props.appId}
                orgId={props.orgId}
                onCancel={() => setShowAddReleasePilotsModal(false)}
            />
            {workflowId && (
                <PilotRotationSettingsModal
                    appId={props.appId}
                    orgId={props.orgId}
                    workflowId={workflowId}
                    open={showPilotRotationSettingsModal}
                    onCancel={() => setShowPilotRotationSettingsModal(false)}
                />
            )}
            {modalContextHolder}
        </Space>
    );
};

export default ReleasePilotRotationTable;

interface DeleteUserFromPilotRotationProps extends AppComponentProps {
    releasePilotId: string;
}

const DeleteUserFromPilotRotationButton: React.FC<DeleteUserFromPilotRotationProps> = (props) => {
    const dispatch = useDispatch();

    const rotationItemDeleteError = useError(AppSettingsUpdatable.RemoveReleasePilot, props.releasePilotId);
    const rotationItemDeleteIsLoading = useIsLoading(AppSettingsUpdatable.RemoveReleasePilot, props.releasePilotId);
    const rotationItemDeleteSuccess = useUpdated(AppSettingsUpdatable.RemoveReleasePilot, props.releasePilotId);

    const [isPopconfirmVisible, setIsPopconfirmVisible] = React.useState(false);

    React.useEffect(() => {
        if (rotationItemDeleteSuccess) {
            globalMessage.success('Removed pilot from release rotation!');
            setIsPopconfirmVisible(false);
        }
    }, [rotationItemDeleteSuccess]);

    React.useEffect(() => {
        if (rotationItemDeleteError) {
            globalMessage.error(rotationItemDeleteError);
        }
    }, [rotationItemDeleteError]);

    return (
        <Popconfirm
            open={isPopconfirmVisible}
            title="Are you sure you want to remove this pilot from the rotation? This change will be updated immediately."
            okText="Yes, remove."
            okButtonProps={{ loading: rotationItemDeleteIsLoading }}
            onConfirm={() => {
                dispatch(
                    tryUpdateAppSettings(
                        AppSettingsUpdatable.RemoveReleasePilot,
                        {
                            orgId: props.orgId,
                            appId: props.appId,
                            releasePilotId: props.releasePilotId,
                        },
                        props.releasePilotId,
                    ),
                );
            }}
            onCancel={() => setIsPopconfirmVisible(false)}
            overlayStyle={{ maxWidth: 400 }}
            placement="topRight"
        >
            <PermissionedActionButton
                actionType={RunwayUserAction.UAUpdateAppSettings}
                icon={<DeleteOutlined style={{ marginRight: 10, color: runwayColors.statusRed }} />}
                type="link"
                onClick={() => {
                    setIsPopconfirmVisible(true);
                }}
                style={{ width: 24, height: 24 }}
            />
        </Popconfirm>
    );
};

interface ReleasePilotRotationProps extends ReleasePilotRotationItem {
    orgId: string;
}

const ReleasePilotRotationRow: React.FC<ReleasePilotRotationProps> = (props) => {
    const releasePilotOrgUser = useSelector((state: RootState) => selectOrgUser(state, props.orgId, props.id));
    const releasePilotDisplayName = displayReleasePilotName(releasePilotOrgUser, props.id, true);

    const overridePilotOrgUser = useSelector((state: RootState) => {
        if (props.overridePilotId) {
            return selectOrgUser(state, props.orgId, props.overridePilotId);
        }

        return null;
    });
    const overridePilotUserName =
        overridePilotOrgUser ? displayUserNameLong(overridePilotOrgUser) : props.overridePilotId;

    if (props.overridePilotId) {
        return (
            <div>
                <Text key={props.id}>{releasePilotDisplayName}</Text>{' '}
                <Text key={props.overridePilotId} style={{ color: antGrey.grey6 }}>
                    {overridePilotUserName}
                </Text>{' '}
                <Tooltip title={'Schedule override'}>
                    <RetweetOutlined style={{ color: antGrey.grey6 }} />
                </Tooltip>
            </div>
        );
    } else {
        return <Text key={props.id}>{releasePilotDisplayName}</Text>;
    }
};

interface ReleasePilotScheduleRotationProps extends ReleasePilotRotationItem {
    orgId: string;
    appId: string;
    isSyncedFromScheduleIntegration: boolean;
    effectiveTimezone: string;
}

const ReleasePilotScheduleRotationRow: React.FC<ReleasePilotScheduleRotationProps> = (props) => {
    const releasePilotOrgUser = useSelector((state: RootState) => selectOrgUser(state, props.orgId, props.id));
    const releasePilotDisplayName = displayReleasePilotName(releasePilotOrgUser, props.id, true);

    const overridePilotOrgUser = useSelector((state: RootState) => {
        if (props.overridePilotId) {
            return selectOrgUser(state, props.orgId, props.overridePilotId);
        }

        return null;
    });
    const overridePilotUserName =
        overridePilotOrgUser ? displayUserNameLong(overridePilotOrgUser) : props.overridePilotId;

    const nonRunwayPilotUserEmail = props.pilotFromScheduleIntegrationEmail;
    const hasScheduleShiftDetails = props.scheduleOnCallShiftStart && props.scheduleOnCallShiftEnd;

    let pilotDetailsNode = null;

    if (props.overridePilotId) {
        pilotDetailsNode = (
            <div>
                <Text style={{ color: antGrey.grey6, textDecoration: 'line-through' }}>
                    {nonRunwayPilotUserEmail ?? releasePilotDisplayName}
                </Text>{' '}
                <Text>{overridePilotUserName}</Text>{' '}
                <Tooltip title={'Schedule override'}>
                    <RetweetOutlined />
                </Tooltip>
            </div>
        );
    } else if (nonRunwayPilotUserEmail) {
        pilotDetailsNode = (
            <Tooltip title="This pilot is not a user in your Runway organization.">
                <Text>{nonRunwayPilotUserEmail}</Text>
            </Tooltip>
        );
    } else if (
        !props.releaseVersionTargetKickoffDate &&
        !props.releaseVersionKickoffDate &&
        props.isSyncedFromScheduleIntegration
    ) {
        pilotDetailsNode = (
            <Tooltip title="The release pilot for this version is predicted based on your scheduling integration's rotation – a pilot will be assigned to the release as soon as a target kickoff date is set or when the release is kicked off.">
                <Text>{releasePilotDisplayName}</Text>
            </Tooltip>
        );
    } else {
        pilotDetailsNode = <Text>{releasePilotDisplayName}</Text>;
    }

    return (
        <Space>
            {pilotDetailsNode}
            <PilotMissingPermissionsWarning appId={props.appId} pilotId={props.overridePilotId ?? props.id} />
            {hasScheduleShiftDetails && (
                <Tooltip
                    title={`On-call from ${extendedDayjs(props.scheduleOnCallShiftStart)
                        .tz(props.effectiveTimezone)
                        .format(
                            DatetimeFormat.MonthAbbreviatedDateTime,
                        )} to ${extendedDayjs(props.scheduleOnCallShiftEnd).tz(props.effectiveTimezone).format(DatetimeFormat.MonthAbbreviatedDateTime)}.`}
                >
                    <InfoCircleOutlined style={{ color: antGrey.grey6 }} />
                </Tooltip>
            )}
        </Space>
    );
};

interface SwapReleasePilotActionProps extends AppComponentProps {
    row: ReleasePilotRotationItem;
    disabled: boolean;
}

type PilotChangeMode = 'override' | 'swap';

const PilotChangeArrow: React.FC = () => <RightOutlined style={{ margin: '0 8px', fontSize: 11 }} />;

const SwapReleasePilotAction: React.FC<SwapReleasePilotActionProps> = (props) => {
    const dispatch = useDispatch();
    const [mode, setMode] = React.useState<PilotChangeMode | null>(null);
    const [selectedPilotId, setSelectedPilotId] = React.useState<string | undefined>(undefined);
    const [swapPreview, setSwapPreview] = React.useState<ReleasePilotSwapPreview | undefined>(undefined);
    const [swapPreviewError, setSwapPreviewError] = React.useState<string | undefined>(undefined);
    const [swapPreviewLoading, setSwapPreviewLoading] = React.useState(false);

    const version = props.row.releaseVersion;
    const currentPilotId = props.row.overridePilotId ?? props.row.id;
    const pilotUsers = useSelector((state: RootState) => selectReleasePilotEligibleUsersForApp(state, props.appId));
    const canUpdateReleasePilot = useSelector((state: RootState) =>
        selectUserCanPerformAction(state, RunwayUserAction.UAUpdateReleasePilot),
    );
    const auth = useSelector((state: RootState) => selectAuthCredentials(state));

    const pilotDisplayName = (pilotId: string | undefined) => {
        if (!pilotId) {
            return '';
        }
        const user = pilotUsers.find((pilotUser) => pilotUser.id === pilotId);
        return user ? displayUserNameLong(user) : pilotId;
    };

    const isLoading = useIsLoading(AppSettingsUpdatable.UpdateReleasePilotOverride, version);
    const updateError = useError(AppSettingsUpdatable.UpdateReleasePilotOverride, version);
    const updateSuccess = useUpdated(AppSettingsUpdatable.UpdateReleasePilotOverride, version);

    const isSwap = mode === 'swap';

    // The backend owns which release is the other half of a swap
    React.useEffect(() => {
        if (!isSwap || !selectedPilotId || !version) {
            setSwapPreview(undefined);
            setSwapPreviewError(undefined);
            return undefined;
        }

        let stale = false;
        setSwapPreviewLoading(true);
        API.previewReleasePilotSwap({ orgId: props.orgId, appId: props.appId, version, pilotId: selectedPilotId }, auth)
            .then((preview) => {
                if (!stale) {
                    setSwapPreview(preview);
                    setSwapPreviewError(undefined);
                }
            })
            .catch((error) => {
                if (!stale) {
                    setSwapPreview(undefined);
                    setSwapPreviewError(error.message);
                }
            })
            .finally(() => {
                if (!stale) {
                    setSwapPreviewLoading(false);
                }
            });

        return () => {
            stale = true;
        };
    }, [isSwap, selectedPilotId, version, props.orgId, props.appId, auth]);

    const closeDialog = () => {
        setMode(null);
        setSelectedPilotId(undefined);
    };

    React.useEffect(() => {
        if (updateSuccess) {
            globalMessage.success(`Release pilot updated for ${version}!`);
            closeDialog();
        }
    }, [updateSuccess]);

    React.useEffect(() => {
        if (updateError) {
            globalMessage.error(updateError);
        }
    }, [updateError]);

    const savePilotChange = () => {
        dispatch(
            tryUpdateAppSettings(
                AppSettingsUpdatable.UpdateReleasePilotOverride,
                {
                    orgId: props.orgId,
                    appId: props.appId,
                    version,
                    pilotTurn: props.row.pilotTurn,
                    pilotId: selectedPilotId,
                    swapWithPilot: mode === 'swap',
                },
                version,
            ),
        );
    };

    if (!version) {
        return <></>;
    }

    const swapIsUnresolved = isSwap && !!selectedPilotId && !!swapPreviewError;

    return (
        <>
            <Dropdown
                disabled={props.disabled || !canUpdateReleasePilot}
                trigger={['click']}
                menu={{
                    items: [
                        { key: 'override', label: `Replace pilot for ${version}` },
                        { key: 'swap', label: `Swap pilot for ${version}` },
                    ],
                    onClick: ({ key }) => setMode(key as PilotChangeMode),
                }}
            >
                <Button
                    icon={<UserSwitchOutlined style={{ color: antGrey.grey7 }} />}
                    type="link"
                    disabled={props.disabled || !canUpdateReleasePilot}
                />
            </Dropdown>
            <Modal
                open={mode !== null}
                title={isSwap ? `Swap pilots for ${version}` : `Replace pilot for ${version}`}
                okText={
                    selectedPilotId ? `Set ${pilotDisplayName(selectedPilotId)} as pilot for ${version}`
                    : isSwap ?
                        'Swap'
                    :   'Replace'
                }
                okButtonProps={{
                    type: 'primary',
                    disabled: !selectedPilotId || (isSwap && (swapPreviewLoading || !swapPreview)),
                    loading: isLoading,
                }}
                cancelText="Cancel"
                onOk={savePilotChange}
                onCancel={closeDialog}
            >
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    <Text type="secondary">
                        {isSwap ?
                            'Choose someone to swap their assignment in the order with this release. The rotation order will be otherwise unchanged.'
                        :   'Choose who should be the new pilot for this release. The rotation order will be otherwise unchanged.'
                        }
                    </Text>
                    <Select
                        placeholder={isSwap ? 'Select a pilot to swap with' : 'Select a pilot'}
                        style={{ width: '100%' }}
                        value={selectedPilotId}
                        onChange={setSelectedPilotId}
                    >
                        {pilotUsers
                            .filter((user) => user.id !== currentPilotId)
                            .map((user) => (
                                <Select.Option key={user.id} value={user.id}>
                                    <Text>{displayUserNameLong(user)}</Text>
                                </Select.Option>
                            ))}
                    </Select>
                    {isSwap && swapPreview && (
                        <Space direction="vertical" size={2}>
                            <Text>
                                <b>{version}</b> - {pilotDisplayName(swapPreview.displacedPilotId)}
                                <PilotChangeArrow />
                                <b>{pilotDisplayName(selectedPilotId)}</b>
                            </Text>
                            <Text>
                                <b>{swapPreview.counterpartVersion}</b> - {pilotDisplayName(selectedPilotId)}
                                <PilotChangeArrow />
                                <b>{pilotDisplayName(swapPreview.displacedPilotId)}</b>
                            </Text>
                        </Space>
                    )}
                    {swapIsUnresolved && (
                        <Text type="danger">
                            {swapPreviewError} You can change this release&apos;s pilot using the Replace pilot action.
                        </Text>
                    )}
                    {!isSwap && selectedPilotId && (
                        <Text>
                            {pilotDisplayName(currentPilotId)}
                            <PilotChangeArrow />
                            <b>{pilotDisplayName(selectedPilotId)}</b>
                        </Text>
                    )}
                </Space>
            </Modal>
        </>
    );
};
